// Shared constants and utilities for Cosmic Clock components
// 
// META-BUNDLE INTEGRATION:
// - Symbols and colors imported from styleCard.js (single source of truth)
// - Dial-specific positioning (zones, angles) defined here
// - See styleCard.js for SYMBOLS.planets and SYMBOLS.zodiac

import { SYMBOLS } from '../utils/styleCard';

// ==========================================
// DIAL ORIENTATION CONSTANTS
// ==========================================
// The dial is oriented like a horizon view:
// - EAST (left, 0°): Spring Equinox (0° Aries) - rising point
// - NORTH (top, 90°): Summer Solstice (0° Cancer) - solar peak
// - WEST (right, 180°): Autumn Equinox (0° Libra) - setting point  
// - SOUTH (bottom, 270°): Winter Solstice (0° Capricorn) - solar nadir
// 
// Above horizon (top half): Spring & Summer - Solar Dominant Hemisphere
// Below horizon (bottom half): Autumn & Winter - Lunar Dominant Hemisphere

// Rotation offset: 0° longitude should appear at LEFT (East/rising position)
// In SVG, 0° is at 3 o'clock (right), so we need -180° offset to move to left
export const DIAL_ROTATION_OFFSET = 180;

// Planet configuration - merges styleCard symbols with dial-specific positioning
// Zone positioning determines where planet hands appear on the dial
const PLANET_DIAL_CONFIG = {
  sun: { zone: 'edge', zonePosition: 1.0, isSun: true, handColorDark: '#B8860B' },
  moon: { zone: 'inner', zonePosition: 0.4, handColorDark: '#A0A0A0' },
  mercury: { zone: 'inner', zonePosition: 0.65, handColorDark: '#6B3A1F' },
  venus: { zone: 'inner', zonePosition: 0.9, handColorDark: '#A08060' },
  mars: { zone: 'outer', zonePosition: 0.15, handColorDark: '#8B3A3A' },
  jupiter: { zone: 'outer', zonePosition: 0.35, handColorDark: '#8B6914' },
  saturn: { zone: 'outer', zonePosition: 0.55, handColorDark: '#8B7355' },
  uranus: { zone: 'outer', zonePosition: 0.7, handColorDark: '#4682B4' },
  neptune: { zone: 'outer', zonePosition: 0.85, handColorDark: '#2B4A9D' },
  pluto: { zone: 'outer', zonePosition: 1.0, handColorDark: '#5C2E0D' },
  dragon: { zone: 'outer', zonePosition: 0.45, handColorDark: '#6A5ACD' }
};

// Build PLANET_CONFIG by merging styleCard symbols with dial positioning
export const PLANET_CONFIG = Object.fromEntries(
  Object.entries(PLANET_DIAL_CONFIG).map(([planet, dialConfig]) => {
    const styleData = SYMBOLS.planets[planet] || { symbol: '•', color: '#888888' };
    return [planet, {
      symbol: styleData.symbol,
      color: styleData.color,
      handColor: dialConfig.handColorDark,
      zone: dialConfig.zone,
      zonePosition: dialConfig.zonePosition,
      isSun: dialConfig.isSun || false
    }];
  })
);

// Calculate hand length based on zone
export const getHandLength = (planet, zones) => {
  const { innerZoneMax = 65, edgeZoneRadius = 135, outerZoneMin = 160, outerZoneMax = 228 } = zones;
  const config = PLANET_CONFIG[planet] || { zone: 'outer', zonePosition: 0.5 };
  
  if (config.zone === 'inner') {
    return 20 + config.zonePosition * (innerZoneMax - 20);
  } else if (config.zone === 'edge') {
    return edgeZoneRadius;
  }
  return outerZoneMin + config.zonePosition * (outerZoneMax - outerZoneMin);
};

// Convert ecliptic longitude to SVG angle (in radians)
// 0° longitude (0° Aries) = 180° SVG (left side/East)
// Longitude increases counter-clockwise on the dial
// For Southern Hemisphere, add 180° offset to invert the view
export const longitudeToDialAngle = (longitude, isSouthernHemisphere = false) => {
  // 0° longitude maps to 180° SVG (left side/East)
  // As longitude increases, angle increases counter-clockwise
  let svgAngle = 180 + longitude;
  
  // Southern Hemisphere: add 180° offset to invert the view
  // This reflects the "view from below the equator" perspective
  if (isSouthernHemisphere) {
    svgAngle += 180;
  }
  
  return svgAngle * (Math.PI / 180);
};

// Convert longitude to x,y position on dial
export const longitudeToPosition = (longitude, radius, isSouthernHemisphere = false) => {
  const angle = longitudeToDialAngle(longitude, isSouthernHemisphere);
  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    angle
  };
};

// Convert SVG degrees to radians for arc/segment drawing
// This is a simple conversion - angles are already in SVG coordinate system
export const svgDegreesToRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

// Generate arc path for curved text
// Angles are in SVG degrees: 0° = right, 90° = bottom, 180° = left, 270° = top
export const createArcPath = (center, radius, startAngle, endAngle) => {
  // Guard against undefined/NaN values
  if (center === undefined || radius === undefined || startAngle === undefined || endAngle === undefined ||
      isNaN(center) || isNaN(radius) || isNaN(startAngle) || isNaN(endAngle)) {
    return 'M 0,0'; // Return valid empty path
  }
  
  const midAngle = ((startAngle + endAngle) / 2 + 360) % 360;
  // Text needs to be flipped for bottom half of circle
  // In SVG: 0° = right, 90° = bottom, 180° = left, 270° = top
  // Bottom half is roughly 0° to 180° (right through bottom to left)
  // We want text readable from inside the circle, so flip when text would appear upside down
  // Include angles near 360° (like 359°) as part of the bottom half for proper text orientation
  const isBottomHalf = (midAngle >= 0 && midAngle < 180) || midAngle > 350;
  
  const startRad = svgDegreesToRadians(startAngle);
  const endRad = svgDegreesToRadians(endAngle);
  
  const x1 = center + Math.cos(startRad) * radius;
  const y1 = center + Math.sin(startRad) * radius;
  const x2 = center + Math.cos(endRad) * radius;
  const y2 = center + Math.sin(endRad) * radius;
  
  return isBottomHalf
    ? `M ${x2},${y2} A ${radius},${radius} 0 0,0 ${x1},${y1}`
    : `M ${x1},${y1} A ${radius},${radius} 0 0,1 ${x2},${y2}`;
};

// Generate segment path (for ring sections)
// Angles are in SVG degrees: 0° = right, 90° = bottom, 180° = left, 270° = top
export const createSegmentPath = (center, innerRadius, outerRadius, startAngle, endAngle) => {
  // Guard against undefined/NaN values
  if (center === undefined || innerRadius === undefined || outerRadius === undefined || 
      startAngle === undefined || endAngle === undefined ||
      isNaN(center) || isNaN(innerRadius) || isNaN(outerRadius) || 
      isNaN(startAngle) || isNaN(endAngle)) {
    return { path: 'M 0,0', divisionLine: { x1: 0, y1: 0, x2: 0, y2: 0 } };
  }
  
  const startRad = svgDegreesToRadians(startAngle);
  const endRad = svgDegreesToRadians(endAngle);
  const segmentAngle = ((endAngle - startAngle) + 360) % 360;
  const largeArcFlag = segmentAngle > 180 ? 1 : 0;

  const x1 = center + Math.cos(startRad) * innerRadius;
  const y1 = center + Math.sin(startRad) * innerRadius;
  const x2 = center + Math.cos(startRad) * outerRadius;
  const y2 = center + Math.sin(startRad) * outerRadius;
  const x3 = center + Math.cos(endRad) * outerRadius;
  const y3 = center + Math.sin(endRad) * outerRadius;
  const x4 = center + Math.cos(endRad) * innerRadius;
  const y4 = center + Math.sin(endRad) * innerRadius;

  return {
    path: `M ${x1},${y1} L ${x2},${y2} A ${outerRadius},${outerRadius} 0 ${largeArcFlag},1 ${x3},${y3} L ${x4},${y4} A ${innerRadius},${innerRadius} 0 ${largeArcFlag},0 ${x1},${y1}`,
    divisionLine: { x1, y1, x2, y2 }
  };
};

// Aspect configuration
export const ASPECT_CONFIG = {
  conjunction: { color: '#FFD700', dash: 'none', opacity: 0.6, width: 2 },
  sextile: { color: '#90EE90', dash: '6 3', opacity: 0.5, width: 1.5 },
  square: { color: '#FF6B6B', dash: '4 4', opacity: 0.5, width: 1.5 },
  trine: { color: '#87CEEB', dash: 'none', opacity: 0.55, width: 2 },
  opposition: { color: '#DDA0DD', dash: '8 4', opacity: 0.5, width: 1.5 }
};

// Season colors
export const SEASON_COLORS = {
  Winter: '#87CEEB', Spring: '#90EE90', Summer: '#FFD700', Autumn: '#CD853F',
  Dry: '#DAA520', Wet: '#4682B4',
  'Polar Day': '#FFD700', 'Polar Night': '#4169E1'
};

// Sabbat data - with dial angles in SVG coordinate system
// East (left/180° SVG) = Spring Equinox (Ostara)
// North (top/270° SVG) = Summer Solstice (Litha)
// West (right/0° SVG) = Autumn Equinox (Mabon)
// South (bottom/90° SVG) = Winter Solstice (Yule)

// Northern Hemisphere Sabbats (the standard wheel of the year)
// These positions are fixed and represent the astronomical/seasonal wheel
// Sabbat names stay the same regardless of hemisphere
export const SABBATS_NORTHERN = [
  { name: 'Yule', day: 355, dialAngle: 90, color: '#1E90FF', type: 'solstice' },       // Winter Solstice - South/Bottom
  { name: 'Imbolc', day: 32, dialAngle: 135, color: '#87CEEB', type: 'cross_quarter' },     // ~Feb 1 - between South and East
  { name: 'Ostara', day: 80, dialAngle: 180, color: '#90EE90', type: 'equinox' },     // Spring Equinox - East/Left
  { name: 'Beltane', day: 121, dialAngle: 225, color: '#FF69B4', type: 'cross_quarter' },   // ~May 1 - between East and North
  { name: 'Litha', day: 172, dialAngle: 270, color: '#FFD700', type: 'solstice' },     // Summer Solstice - North/Top
  { name: 'Lammas', day: 213, dialAngle: 315, color: '#DAA520', type: 'cross_quarter' },    // ~Aug 1 - between North and West
  { name: 'Mabon', day: 264, dialAngle: 0, color: '#CD853F', type: 'equinox' },       // Autumn Equinox - West/Right
  { name: 'Samhain', day: 305, dialAngle: 45, color: '#8B4513', type: 'cross_quarter' }     // ~Nov 1 - between West and South
];

// Standard sabbats - always use Northern positions (anchored to solar cycle)
export const SABBATS = SABBATS_NORTHERN;

// Helper to get sabbats - always returns standard wheel
// The ring visuals stay anchored to the solar cycle for all hemispheres
// Highlight calculation in SabbatsRing.js handles hemisphere-specific display
export const getSabbatsForHemisphere = (isSouthern) => {
  return SABBATS_NORTHERN;
};

// Convert day of year to dial angle (SVG coordinate system)
// Spring Equinox (~day 80) is at 180° SVG (East/left)
export const dayOfYearToDialAngle = (dayOfYear) => {
  const springEquinoxDay = 80;
  const dayOffset = dayOfYear - springEquinoxDay;
  // Progress counter-clockwise: adding days increases angle
  const angle = 180 + (dayOffset / 365) * 360;
  return ((angle % 360) + 360) % 360; // Normalize to 0-360
};

// Zodiac signs - positioned for horizon-oriented dial
// Our dial orientation (as viewed like a compass):
// - EAST (left side) = Spring Equinox = 0° Aries
// - NORTH (top) = Summer Solstice = 0° Cancer
// - WEST (right side) = Autumn Equinox = 0° Libra
// - SOUTH (bottom) = Winter Solstice = 0° Capricorn
//
// In SVG coordinate system:
// - 0° SVG = right (3 o'clock)
// - 90° SVG = bottom (6 o'clock)
// - 180° SVG = left (9 o'clock)
// - 270° SVG = top (12 o'clock)
//
// So our mapping:
// - East (left) = 180° SVG
// - North (top) = 270° SVG
// - West (right) = 0° SVG
// - South (bottom) = 90° SVG
//
// Signs progress counter-clockwise (as viewed from above North Pole)

// Dial angles for zodiac positions (dial-specific, not in styleCard)
const ZODIAC_DIAL_ANGLES = {
  aries: { angle: 180, longitude: 0 },      // East - Spring Equinox (LEFT)
  taurus: { angle: 210, longitude: 30 },
  gemini: { angle: 240, longitude: 60 },
  cancer: { angle: 270, longitude: 90 },    // North - Summer Solstice (TOP)
  leo: { angle: 300, longitude: 120 },
  virgo: { angle: 330, longitude: 150 },
  libra: { angle: 0, longitude: 180 },      // West - Autumn Equinox (RIGHT)
  scorpio: { angle: 30, longitude: 210 },
  sagittarius: { angle: 60, longitude: 240 },
  capricorn: { angle: 90, longitude: 270 }, // South - Winter Solstice (BOTTOM)
  aquarius: { angle: 120, longitude: 300 },
  pisces: { angle: 150, longitude: 330 }
};

// Build ZODIAC_SIGNS by merging styleCard symbols with dial angles
export const ZODIAC_SIGNS = Object.entries(SYMBOLS.zodiac).map(([key, data]) => ({
  name: data.name,
  glyph: data.symbol,
  angle: ZODIAC_DIAL_ANGLES[key]?.angle ?? 0,
  longitude: ZODIAC_DIAL_ANGLES[key]?.longitude ?? 0
}));
